<template>
  <div class="wooden-fish" @mousedown.left="handleHit" @touchstart.prevent="handleHit">
    <el-card shadow="hover" class="panel">
      <div class="header">
        <div class="title">
          <span class="emoji">🔔</span>
          <span>Wooden Fish</span>
        </div>
        <div class="counts">
          <el-tag type="success" effect="dark" class="count-tag">
            sum: {{ formatted(totalCount) }}
          </el-tag>
          <el-tag type="info" class="count-tag">
            current: {{ formatted(sessionCount) }}
          </el-tag>
          <el-tag v-if="goal > 0" :type="goalReached ? 'success' : 'warning'" class="count-tag">
            target: {{ sessionCount }} / {{ goal }}
          </el-tag>
        </div>
      </div>

      <el-row :gutter="16" class="controls">
        <el-col :xs="24" :sm="12" :md="8">
          <div class="ctrl-item">
            <span class="ctrl-label">Voice</span>
            <el-switch v-model="soundOn" active-text="On" inactive-text="Off"/>
          </div>
          <div class="ctrl-item">
            <span class="ctrl-label">Volume</span>
            <el-slider v-model="volume" :min="0" :max="1" :step="0.05" :show-stops="true" :stop-count="10"/>                
          </div>
          <div class="ctrl-item">
            <span class="ctrl-label">Merit per Hit</span>
            <el-input-number v-model="delta" :min="1" :max="99" :step="1"/>
          </div>
        </el-col>

        <el-col :xs="24" :sm="12" :md="8">
          <div class="ctrl-item">
            <span class="ctrl-label">Target</span>
            <el-input-number v-model="goal" :min="0" :step="100" :max="99999999"/>
          </div>
          <div class="ctrl-item">
            <span class="ctrl-label">My Wish</span>
            <el-input v-model="wish" placeholder="Enter your wish" maxlength="50" show-word-limit/>
          </div>
          <div class="ctrl-item">
            <el-button type="primary" icon="el-icon-thumb" @click.stop="handleHit" round class="hit-btn" :disabled="hitting">
              Hit (Space)
            </el-button>
            <el-button @click="resetSession" icon="el-icon-refresh" round>Reset</el-button>
          </div>
        </el-col>

        <el-col :xs="24" :sm="24" :md="8">
          <div class="ctrl-item">
            <span class="ctrl-label">Auto Merit</span>
            <el-switch v-model="auto" active-text="On" inactive-text="Off"/>
          </div>
          <div class="ctrl-item">
            <span class="ctrl-label">Speed (times/sec)</span>
            <el-input-number v-model="autoRate" :min="0.2" :max="20" :step="0.2" :disabled="!auto"/>
          </div>
          <div class="ctrl-item">
            <span class="ctrl-label">Zen Mode</span>
            <el-button @click="enterZen" icon="el-icon-full-screen" round>Enter</el-button>
          </div>
        </el-col>
      </el-row>

      <el-progress
        v-if="goal > 0"
        :text-inside="true"
        :stroke-width="20"
        :percentage="progress"
        :status="goalReached ? 'success' : undefined"
        class="goal-progress"
      />

      <div class="wish-line" v-if="wish">
        <el-alert :title="'Wish: ' + wish" type="info" :closable="false" show-icon/>
      </div>

      <div class="fish-area">
        <div class="fish-container">
          <div class="fish" :class="{ pulse: pulse }" @click.stop="handleHit">
            <img src="@/assets/woodenFish.png" alt="Wooden Fish" class="fish-img">
            <div class="ripple" :class="{ active: pulse }"></div>
            <div class="merit" :class="{ show: pulse }">+{{ delta }}</div>
          </div>
        </div>
        <div class="hint">Click or press Space to hit the wooden fish</div>
      </div>
    </el-card>

    <!-- 沉浸模式 -->
    <transition name="fade">
      <div v-if="zen" class="zen" @click="exitZen">
        <div class="zen-info">
          <div class="zen-count">{{ formatted(sessionCount) }}</div>
          <div class="zen-wish" v-if="wish">Wish: {{ wish }}</div>
          <div class="zen-hint">Click anywhere to exit</div>
        </div>
        <div class="zen-fish" :class="{ pulse: pulse }" @click.stop="handleHit">
          <img src="@/assets/woodenFish.png" alt="Wooden Fish" class="fish-img">
          <div class="ripple" :class="{ active: pulse }"></div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'WoodenFish',
  data() {
    return {
      totalCount: 0,
      sessionCount: 0,
      goal: 1000,
      wish: '',
      delta: 1,
      soundOn: true,
      volume: 0.6,
      auto: false,
      autoRate: 2, // 次/秒
      autoTimer: null,
      zen: false,
      pulse: false,
      hitting: false,
      spaceDown: false,
      ctx: null,
      storageKey: 'wooden-fish-v1'
      // Removed mitt instance
    }
  },
  computed: {
    progress() {
      if (!this.goal) return 0
      const p = Math.min(100, Math.floor(this.sessionCount / this.goal * 100))
      return p
    },
    goalReached() {
      return this.goal > 0 && this.sessionCount >= this.goal
    }
  },
  mounted() {
    this.restore()
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
    // Removed mitt $once replacement
  },
  // Replaced beforeDestroy with beforeUnmount
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
    this.stopAuto()
  },
  watch: {
    auto(val) {
      if (val) this.startAuto(); else this.stopAuto()
    },
    autoRate() {
      if (this.auto) {
        this.stopAuto();
        this.startAuto();
      }
    },
    totalCount: 'persist',
    sessionCount: 'persist',
    goal: 'persist',
    wish: 'persist',
    delta: 'persist',
    soundOn: 'persist',
    volume: 'persist'
  },
  methods: {
    formatted(n) {
      return n.toLocaleString()
    },
    handleHit() {
      if (this.hitting) return
      this.hitting = true
      this.sessionCount += this.delta
      this.totalCount += this.delta
      this.pulse = true
      if (this.soundOn) this.playWooden()
      setTimeout(() => {
        this.pulse = false
        this.hitting = false
      }, 90)
    },
    onKeyDown(e) {
      if (e.code === 'Space') {
        if (!this.spaceDown) {
          e.preventDefault()
          this.spaceDown = true
          this.handleHit()
        } else {
          e.preventDefault()
        }
      }
    },
    onKeyUp(e) {
      if (e.code === 'Space') {
        e.preventDefault()
        this.spaceDown = false
      }
    },
    startAuto() {
      const interval = Math.max(10, Math.floor(1000 / this.autoRate))
      this.autoTimer = setInterval(() => {
        this.handleHit()
      }, interval)
    },
    stopAuto() {
      if (this.autoTimer) {
        clearInterval(this.autoTimer)
        this.autoTimer = null
      }
    },
    enterZen() {
      this.zen = true
      const el = document.documentElement
      if (el.requestFullscreen) {
        el.requestFullscreen().catch(error => {
          console.error('Error attempting to enable full-screen mode:', error)
        })
      }
    },
    exitZen() {
      this.zen = false
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(error => {
          console.error('Error attempting to exit full-screen mode:', error)
        })
      }
    },
    playWooden() {
      try {
        if (!this.ctx) this.ctx = new (window.AudioContext || window.webkitAudioContext)()
        const ctx = this.ctx
        const now = ctx.currentTime

        // 低频主体
        const o1 = ctx.createOscillator()
        const g1 = ctx.createGain()
        o1.type = 'sine'
        o1.frequency.setValueAtTime(420, now) // 木鱼偏高的木质音
        g1.gain.setValueAtTime(0.0001, now)
        g1.gain.exponentialRampToValueAtTime(this.volume, now + 0.005)
        g1.gain.exponentialRampToValueAtTime(0.0001, now + 0.12)
        o1.connect(g1).connect(ctx.destination)

        // 叠加瞬态“击木”感
        const o2 = ctx.createOscillator()
        const g2 = ctx.createGain()
        o2.type = 'triangle'
        o2.frequency.setValueAtTime(1000, now)
        g2.gain.setValueAtTime(0.0001, now)
        g2.gain.exponentialRampToValueAtTime(this.volume * 0.6, now + 0.003)
        g2.gain.exponentialRampToValueAtTime(0.0001, now + 0.06)
        o2.connect(g2).connect(ctx.destination)

        o1.start(now); o1.stop(now + 0.14)
        o2.start(now); o2.stop(now + 0.08)
      } catch (e) {
        console.error('Error playing wooden fish sound:', e)
      }
    },
    resetSession() {
      this.$confirm('This will only reset the current session count. Continue?', 'Confirm', {
        confirmButtonText: 'Reset',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        this.sessionCount = 0
      }).catch(() => { console.error('Failed to reset session count') })
    },
    persist() {
      const data = {
        totalCount: this.totalCount,
        sessionCount: this.sessionCount,
        goal: this.goal,
        wish: this.wish,
        delta: this.delta,
        soundOn: this.soundOn,
        volume: this.volume
      }
      try { localStorage.setItem(this.storageKey, JSON.stringify(data)) } catch (e) { console.error('Failed to persist data:', e) }
    },
    restore() {
      try {
        const raw = localStorage.getItem(this.storageKey)
        if (!raw) return
        const data = JSON.parse(raw)
        const keys = ['totalCount','sessionCount','goal','wish','delta','soundOn','volume']
        keys.forEach(k => { if (data[k] !== undefined) this[k] = data[k] })
      } catch (e) { console.error('Failed to restore data:', e) }
    }
  }
}
</script>

<style scoped>
.wooden-fish {
  max-width: 980px;
  margin: 24px auto;
  padding: var(--spacing-lg);
}

.panel {
  border-radius: var(--radius-lg);
  background: var(--bg-card);
  box-shadow: var(--shadow-md);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-sm);
}

.title {
  font-size: 20px;
  font-weight: 600;
  font-family: var(--font-heading);
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.emoji { font-size: 24px; }

.counts {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.count-tag { font-size: 14px; }

.controls { margin-top: var(--spacing-md); }

.ctrl-item {
  margin: 12px 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.ctrl-label {
  width: 120px;
  color: var(--text-secondary);
  font-family: var(--font-body);
}

.hit-btn { font-weight: 600; }

.goal-progress { margin-top: var(--spacing-sm); }

.wish-line { margin-top: var(--spacing-sm); }

.fish-area {
  margin: 32px 0 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: transparent;
}

.fish-container {
  position: relative;
  perspective: 1000px;
  background: transparent;
}

/* 木鱼主体 */
.fish {
  position: relative;
  width: 260px;
  height: 260px;
  cursor: pointer;
  transition: all 120ms cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 15px 35px rgba(0, 0, 0, 0.3));
}

.fish-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
}

/* 敲击动效 */
.fish.pulse {
  transform: scale(0.92) translateY(5px);
  filter: drop-shadow(0 8px 20px rgba(0, 0, 0, 0.4));
}

.fish:hover {
  transform: scale(1.05) translateY(-3px);
  filter: drop-shadow(0 20px 45px rgba(0, 0, 0, 0.35));
}

/* 涟漪效果 - 金色光晕 */
.ripple {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid rgba(255, 215, 0, 0.7);
  transform: translate(-50%, -50%) scale(0.8);
  opacity: 0;
  pointer-events: none;
  box-shadow: 0 0 20px rgba(255, 215, 0, 0.5);
}

.ripple.active {
  animation: rippleOut 600ms ease-out;
}

@keyframes rippleOut {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 1;
    border-width: 3px;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.9);
    opacity: 0;
    border-width: 1px;
  }
}

/* 功德数字 - 金色 */
.merit {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0);
  font-weight: 800;
  font-size: 48px;
  font-family: var(--font-heading);
  background: linear-gradient(135deg, #ffd700, #ffed4e, #ffd700);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 3px 8px rgba(0, 0, 0, 0.4))
          drop-shadow(0 0 25px rgba(255, 215, 0, 0.6));
  pointer-events: none;
  opacity: 0;
  z-index: 10;
}

.merit.show {
  animation: meritFloat 600ms cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes meritFloat {
  0% {
    transform: translate(-50%, -50%) scale(0) rotate(-15deg);
    opacity: 0;
  }
  30% {
    opacity: 1;
  }
  50% {
    transform: translate(-50%, -80px) scale(1.3) rotate(5deg);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -120px) scale(0.8) rotate(0deg);
    opacity: 0;
  }
}

.hint {
  margin-top: 40px;
  color: var(--text-disabled);
  font-size: 13px;
  font-family: var(--font-body);
}

/* 沉浸模式 */
.zen {
  position: fixed;
  inset: 0;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1410 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.zen-info {
  position: absolute;
  top: 8vh;
  text-align: center;
  color: #ddd;
}

.zen-count {
  font-size: 72px;
  font-weight: 800;
  letter-spacing: 2px;
  font-family: var(--font-heading);
  background: linear-gradient(135deg, #d4a574, #b8894f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 4px 12px rgba(212, 165, 116, 0.3);
}

.zen-wish {
  margin-top: 12px;
  font-size: 18px;
  color: #aab;
  font-family: var(--font-body);
}

.zen-hint {
  margin-top: 8px;
  font-size: 13px;
  color: #667;
  font-family: var(--font-body);
}

.zen-fish {
  position: relative;
  width: min(50vh, 500px);
  height: min(50vh, 500px);
  cursor: pointer;
  transition: all 120ms cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 30px 60px rgba(0, 0, 0, 0.45));
}

.zen-fish .fish-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
}

.zen-fish.pulse {
  transform: scale(0.93) translateY(10px);
  filter: drop-shadow(0 15px 35px rgba(0, 0, 0, 0.5));
}

.zen-fish:hover {
  transform: scale(1.05) translateY(-5px);
  filter: drop-shadow(0 35px 70px rgba(0, 0, 0, 0.42));
}

.zen-fish .ripple {
  border-color: rgba(255, 215, 0, 0.85);
  border-width: 5px;
  box-shadow: 0 0 30px rgba(255, 215, 0, 0.6);
}

/* 过渡 */
.fade-enter-active, .fade-leave-active { transition: opacity 200ms ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
</style>
